import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw } from 'lucide-react';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { formatDateTime, formatEurFromCents, formatNumber } from '@/lib/format-es';
import { notifyErr, notifyOk } from '@/lib/notify';
import { storeStatusLabel } from '@/lib/store-status';
import { apiClient } from '@/services/api-client';
import { dbClient } from '@/services/db-client';

function unwrap<T>(data: T | T[] | null | undefined): T | null {
    if (data == null) return null;
    return (Array.isArray(data) ? data[0] : data) ?? null;
}

function fmtEur(cents: number): string {
    return formatEurFromCents(cents || 0);
}

function fmtDate(iso: string | null | undefined): string {
    return formatDateTime(iso);
}

type SecondLifeSettings = {
    enabled?: boolean;
    publishEnabled?: boolean;
    checkoutLiveEnabled?: boolean;
};

type SecondLifeSettingsRow = {
    ok?: boolean;
    error?: string;
    settings?: SecondLifeSettings;
    activeListings?: number;
    openReports?: number;
};

type AdminOrder = {
    id: string;
    status: string;
    sandbox?: boolean;
    destinationZone?: string;
    itemsSubtotalCents?: number;
    shippingCents?: number;
    totalCents?: number;
    trackingCode?: string | null;
    absorbUsed?: boolean;
    paidAt?: string | null;
    createdAt?: string;
    buyerName?: string;
    sellerName?: string;
    items?: Array<{ title?: string; priceCents?: number }>;
};

export function SegundaVidaPanel() {
    const queryClient = useQueryClient();
    const [note, setNote] = useState('');
    const [trackingDraft, setTrackingDraft] = useState<Record<string, string>>({});
    const [diffDraft, setDiffDraft] = useState<Record<string, string>>({});
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const settingsQuery = useQuery({
        queryKey: ['admin', 'second-life', 'settings'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<SecondLifeSettingsRow>('admin_get_second_life_settings');
            if (error) throw new Error(error.message ?? 'No se pudo leer');
            const row = unwrap<SecondLifeSettingsRow>(data);
            if (row && row.ok === false) throw new Error(String(row.error || 'No autorizado'));
            return row;
        },
    });

    const reportsQuery = useQuery({
        queryKey: ['admin', 'second-life', 'reports'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<{ items?: Array<Record<string, unknown>> }>(
                'admin_list_second_life_reports'
            );
            if (error) throw new Error(error.message ?? 'No se pudieron leer las denuncias');
            return unwrap(data)?.items ?? [];
        },
    });

    const ordersQuery = useQuery({
        queryKey: ['admin', 'second-life', 'orders'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<{ ok?: boolean; items?: AdminOrder[]; error?: string }>(
                'admin_list_second_life_orders',
                { p_limit: 50 }
            );
            if (error) throw new Error(error.message ?? 'No se pudieron leer los pedidos');
            const row = unwrap(data);
            if (row && row.ok === false) throw new Error(row.error || 'No autorizado');
            return row?.items ?? [];
        },
    });

    const save = useMutation({
        mutationFn: async (patch: Record<string, unknown>) => {
            const current = settingsQuery.data?.settings ?? {};
            const { data, error } = await dbClient.rpc('admin_update_second_life_settings', {
                p_content: JSON.stringify({ ...current, ...patch }),
            });
            if (error) throw new Error(error.message ?? 'No se guardó');
            const row = unwrap(data as { ok?: boolean; error?: string });
            if (row && row.ok === false) throw new Error(row.error || 'No se guardó');
        },
        onSuccess: () => {
            notifyOk('Ajustes guardados');
            void queryClient.invalidateQueries({ queryKey: ['admin', 'second-life'] });
        },
        onError: (err) => {
            notifyErr(err instanceof Error ? err.message : 'No se guardó');
        },
    });

    const suspend = useMutation({
        mutationFn: async (listingId: string) => {
            const { data, error } = await dbClient.rpc('admin_suspend_second_life_listing', {
                p_id: listingId,
                p_notes: note || 'Denuncia revisada',
            });
            if (error) throw new Error(error.message ?? 'No se suspendió');
            const row = unwrap(data as { ok?: boolean; error?: string });
            if (row && row.ok === false) throw new Error(row.error || 'No se suspendió');
        },
        onSuccess: () => {
            notifyOk('Anuncio suspendido');
            void queryClient.invalidateQueries({ queryKey: ['admin', 'second-life'] });
        },
        onError: (err) => {
            notifyErr(err instanceof Error ? err.message : 'No se suspendió');
        },
    });

    const orderAction = useMutation({
        mutationFn: async (body: {
            orderId: string;
            action: string;
            trackingCode?: string;
            extraCents?: number;
            reason?: string;
        }) => {
            const { data, error } = await apiClient.post<{ ok?: boolean; error?: string }>(
                '/second-life/order-action',
                body
            );
            if (error) throw new Error(error.message);
            if (data && data.ok === false) throw new Error(data.error || 'No se pudo actualizar');
            return data;
        },
        onSuccess: () => {
            notifyOk('Pedido actualizado');
            void queryClient.invalidateQueries({ queryKey: ['admin', 'second-life', 'orders'] });
        },
        onError: (err) => {
            notifyErr(err instanceof Error ? err.message : 'No se pudo actualizar');
        },
    });

    const settings = settingsQuery.data?.settings ?? {};
    const orders = ordersQuery.data ?? [];
    const selected = orders.find((o) => o.id === selectedId) ?? null;

    return (
        <div className="space-y-8">
            <section className="space-y-3">
                <p className="text-sm text-muted-foreground">
                    Cobro real con Stripe. El porte usa las tarifas de Envíos de la Tienda. El vendedor ve el importe
                    como saldo pendiente de Segunda Vida hasta confirmar entrega.
                </p>
                {settingsQuery.isLoading ? <p>Cargando…</p> : null}
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        className="rounded-md border px-3 py-2 text-sm"
                        onClick={() => save.mutate({ enabled: !settings.enabled })}
                    >
                        Visible: {settings.enabled ? 'sí' : 'no'}
                    </button>
                    <button
                        type="button"
                        className="rounded-md border px-3 py-2 text-sm"
                        onClick={() => save.mutate({ publishEnabled: !settings.publishEnabled })}
                    >
                        Publicar: {settings.publishEnabled ? 'sí' : 'no'}
                    </button>
                    <button
                        type="button"
                        className="rounded-md border px-3 py-2 text-sm"
                        onClick={() => save.mutate({ checkoutLiveEnabled: !settings.checkoutLiveEnabled })}
                    >
                        Cobro Stripe: {settings.checkoutLiveEnabled ? 'sí' : 'no'}
                    </button>
                </div>
                <p className="text-sm">
                    Anuncios activos: {formatNumber(settingsQuery.data?.activeListings ?? 0)}. Denuncias abiertas:{' '}
                    {formatNumber(settingsQuery.data?.openReports ?? 0)}.
                </p>
            </section>

            <section className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <h2 className="text-base font-semibold">Pedidos</h2>
                    <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs"
                        onClick={() => void ordersQuery.refetch()}
                        disabled={ordersQuery.isFetching}
                    >
                        {ordersQuery.isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                        Actualizar
                    </button>
                </div>
                {ordersQuery.isLoading ? <p className="text-sm text-muted-foreground">Cargando pedidos…</p> : null}
                {ordersQuery.error ? (
                    <p className="text-sm text-destructive">
                        {ordersQuery.error instanceof Error ? ordersQuery.error.message : 'Error'}
                    </p>
                ) : null}
                {orders.length === 0 && !ordersQuery.isLoading ? (
                    <p className="text-sm text-muted-foreground">Todavía no hay pedidos de Segunda Vida.</p>
                ) : null}
                <ul className="divide-y rounded-lg border">
                    {orders.map((order) => (
                        <li key={order.id}>
                            <button
                                type="button"
                                className={`flex w-full flex-col gap-1 px-3 py-3 text-left text-sm hover:bg-muted/40 ${
                                    selectedId === order.id ? 'bg-muted/50' : ''
                                }`}
                                onClick={() => setSelectedId(order.id)}
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <span className="font-medium inline-flex flex-wrap items-center gap-2">
                                        <StatusBadge status={order.status} label={storeStatusLabel(order.status)} size="sm" />
                                        {order.sandbox ? 'prueba' : ''}
                                    </span>
                                    <span className="tabular-nums font-semibold">{fmtEur(Number(order.totalCents ?? 0))}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {order.buyerName} → {order.sellerName} · {fmtDate(order.createdAt)} ·{' '}
                                    {order.destinationZone}
                                </p>
                                <p className="truncate text-xs text-muted-foreground">
                                    {(order.items ?? []).map((i) => i.title).filter(Boolean).join(' · ') || 'Sin líneas'}
                                </p>
                            </button>
                        </li>
                    ))}
                </ul>

                {selected ? (
                    <div className="space-y-3 rounded-lg border p-4">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                                <StatusBadge status={selected.status} label={storeStatusLabel(selected.status)} size="md" />
                                <p className="text-xs text-muted-foreground font-mono">{selected.id}</p>
                            </div>
                            <p className="text-lg font-semibold tabular-nums">{fmtEur(Number(selected.totalCents ?? 0))}</p>
                        </div>
                        <p className="text-sm">
                            Comprador: {selected.buyerName}. Vendedor: {selected.sellerName}.
                        </p>
                        <p className="text-sm text-muted-foreground">
                            Artículos {fmtEur(Number(selected.itemsSubtotalCents ?? 0))} · porte{' '}
                            {fmtEur(Number(selected.shippingCents ?? 0))}
                            {selected.absorbUsed ? ' · absorb usado' : ''}
                        </p>
                        <p className="text-sm">Tracking: {selected.trackingCode || 'sin registrar'}</p>

                        <div className="flex flex-wrap gap-2">
                            <input
                                value={trackingDraft[selected.id] ?? selected.trackingCode ?? ''}
                                onChange={(e) =>
                                    setTrackingDraft((prev) => ({ ...prev, [selected.id]: e.target.value }))
                                }
                                placeholder="Código de tracking"
                                className="min-w-[12rem] flex-1 rounded-md border px-3 py-2 text-sm"
                            />
                            <button
                                type="button"
                                className="rounded-md border px-3 py-2 text-sm"
                                disabled={orderAction.isPending}
                                onClick={() =>
                                    orderAction.mutate({
                                        orderId: selected.id,
                                        action: 'register_tracking',
                                        trackingCode: trackingDraft[selected.id] ?? selected.trackingCode ?? '',
                                    })
                                }
                            >
                                Guardar tracking
                            </button>
                            {['paid', 'seller_confirmed'].includes(selected.status) ? (
                                <button
                                    type="button"
                                    className="rounded-md border px-3 py-2 text-sm"
                                    disabled={orderAction.isPending}
                                    onClick={() =>
                                        orderAction.mutate({ orderId: selected.id, action: 'mark_delivered' })
                                    }
                                >
                                    Marcar entregado
                                </button>
                            ) : null}
                            {selected.status === 'pending_payment' ? (
                                <button
                                    type="button"
                                    className="rounded-md border px-3 py-2 text-sm text-destructive"
                                    disabled={orderAction.isPending}
                                    onClick={() =>
                                        orderAction.mutate({ orderId: selected.id, action: 'admin_cancel_pending' })
                                    }
                                >
                                    Cancelar pendiente
                                </button>
                            ) : null}
                            {!['pending_payment', 'cancelled', 'refunded'].includes(selected.status) ? (
                                <button
                                    type="button"
                                    className="rounded-md border px-3 py-2 text-sm text-destructive"
                                    disabled={orderAction.isPending}
                                    onClick={() =>
                                        orderAction.mutate({
                                            orderId: selected.id,
                                            action: 'admin_refund',
                                            reason: note || 'Refund admin Segunda Vida',
                                        })
                                    }
                                >
                                    Reembolsar (wallet)
                                </button>
                            ) : null}
                        </div>

                        <div className="flex flex-wrap gap-2 border-t pt-3">
                            <input
                                value={diffDraft[selected.id] ?? ''}
                                onChange={(e) => setDiffDraft((prev) => ({ ...prev, [selected.id]: e.target.value }))}
                                placeholder="Extra porte (céntimos)"
                                inputMode="numeric"
                                className="w-40 rounded-md border px-3 py-2 text-sm"
                            />
                            <button
                                type="button"
                                className="rounded-md border px-3 py-2 text-sm"
                                disabled={orderAction.isPending}
                                onClick={() =>
                                    orderAction.mutate({
                                        orderId: selected.id,
                                        action: 'shipping_difference',
                                        extraCents: Math.max(0, Math.floor(Number(diffDraft[selected.id]) || 0)),
                                        reason: note || 'Diferencia de porte',
                                    })
                                }
                            >
                                Aplicar diff porte
                            </button>
                        </div>
                    </div>
                ) : null}
            </section>

            <section className="space-y-3">
                <h2 className="text-base font-semibold">Denuncias</h2>
                <label className="block text-sm" htmlFor="sl-note">
                    Nota al suspender / reembolsar
                </label>
                <input
                    id="sl-note"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    className="w-full max-w-md rounded-md border px-3 py-2 text-sm"
                />
                <ul className="space-y-2">
                    {(reportsQuery.data ?? []).map((report) => (
                        <li key={String(report.id)} className="rounded-md border p-3 text-sm">
                            <p className="font-medium">{String(report.title)}</p>
                            <p>{String(report.reason)}</p>
                            <button
                                type="button"
                                className="mt-2 rounded-md border px-3 py-1"
                                onClick={() => suspend.mutate(String(report.listingId))}
                            >
                                Suspender anuncio
                            </button>
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
}
